import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatMoney, formatDate } from "@/lib/format";
import TransactionDialog from "@/components/TransactionDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, ArrowUpRight, ArrowDownRight } from "lucide-react";

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [a, c] = await Promise.all([api.get("/transactions"), api.get("/categories")]);
    setItems(a.data);
    setCats(c.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const catColor = (id) => cats.find((c) => c.id === id)?.color || "#64748B";

  const filtered = useMemo(() => {
    return items.filter((t) => {
      if (typeFilter !== "all" && t.type !== typeFilter) return false;
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = `${t.description || ""} ${t.counterparty || ""} ${catName(t.category_id)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, typeFilter, statusFilter, query, cats]);

  const del = async (id) => {
    if (!window.confirm("İşlem silinsin mi?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Hareketler</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Gelir & Giderler</h1>
          <p className="text-sm text-slate-400 mt-1">Tüm işlemlerinizi buradan yönetin.</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="transactions-new-button"
          className="bg-indigo-500 hover:bg-indigo-600"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Yeni İşlem
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="sm:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Açıklama, müşteri veya kategori ara..."
            data-testid="transactions-search"
            className="pl-9 bg-[#131924] border-white/10"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="transactions-type-filter" className="bg-[#131924] border-white/10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
            <SelectItem value="all">Tüm Türler</SelectItem>
            <SelectItem value="income">Gelir</SelectItem>
            <SelectItem value="expense">Gider</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="transactions-status-filter" className="bg-[#131924] border-white/10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="completed">Gerçekleşen</SelectItem>
            <SelectItem value="pending">Yaklaşan</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#131924] border border-white/5 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-white/5">
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Tür</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Açıklama</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Kategori</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Tarih</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Durum</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Tutar</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow className="hover:bg-transparent border-white/5">
                <TableCell colSpan={7} className="text-center text-slate-500 py-10">
                  Henüz işlem yok ya da filtre sonucu boş.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t) => (
                <TableRow key={t.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`tx-row-${t.id}`}>
                  <TableCell>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.type === "income" ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
                      {t.type === "income" ? <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" /> : <ArrowDownRight className="w-3.5 h-3.5 text-rose-400" />}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="text-sm font-medium truncate">{t.description || t.counterparty || "—"}</div>
                    {t.counterparty && t.description && (
                      <div className="text-[11px] text-slate-500 truncate">{t.counterparty}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
                      style={{ background: catColor(t.category_id) + "22", color: catColor(t.category_id) }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: catColor(t.category_id) }} />
                      {catName(t.category_id)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-300 font-mono-num">{formatDate(t.date)}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                      t.status === "completed" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"
                    }`}>
                      {t.status === "completed" ? "Gerçekleşen" : "Yaklaşan"}
                    </span>
                  </TableCell>
                  <TableCell className={`text-right font-mono-num font-semibold ${t.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                    {t.type === "income" ? "+" : "-"}{formatMoney(t.amount, t.currency)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setEditing(t); setOpen(true); }}
                        data-testid={`tx-edit-${t.id}`}
                        className="h-8 w-8 text-slate-400 hover:text-slate-100"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => del(t.id)}
                        data-testid={`tx-delete-${t.id}`}
                        className="h-8 w-8 text-slate-400 hover:text-rose-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TransactionDialog open={open} onOpenChange={setOpen} onSaved={load} initial={editing} />
    </div>
  );
}
